import { useEffect, useMemo, useState } from 'react';
import { useBulkUploadTutorial } from '@/features/tutorial/bulkUpload/useBulkUploadTutorial';
import { useSaveSettingsTutorial } from '@/features/tutorial/saveSettings/useSaveSettingsTutorial';
import { useTutorialService } from '@/layout/appWrapper/ServiceProvider';
import { useRecoilState, useRecoilValue } from 'recoil';
import { isLoggedInSelector } from '@/stores/authStore';
import settingsState from '@/stores/settingsStore';
import { tutorialState } from '@/stores/tutorialStore';
import { bulkMediaListState } from '@/stores/mediaStore';
import { useNewModelsTutorial } from '@/features/tutorial/newModels/useNewModelsTutorial';

export const useOrderedTutorials = ({ isVideo }: { isVideo: boolean }) => {
  const settings = useRecoilValue(settingsState);
  const mediaList = useRecoilValue(bulkMediaListState);
  const [tutorial, setTutorial] = useRecoilState(tutorialState);

  const [isAiSettingsDropdownOpen, setIsAiSettingsDropdownOpen] =
    useState(false);
  const { bulkUploadTutorialId, bulkUploadTutorialSteps } =
    useBulkUploadTutorial();
  const { saveSettingsTutorialId, saveSettingsTutorialSteps } =
    useSaveSettingsTutorial({
      setSaveSettingsDropdownOpen: setIsAiSettingsDropdownOpen,
    });
  const { newModelsTutorialId, newModelsTutorialSteps } =
    useNewModelsTutorial();

  const {
    getTutorialCompletedFromLocalStorage,
    setTutorialCompletedInLocalStorage,
  } = useTutorialService();

  const isLoggedIn = useRecoilValue(isLoggedInSelector);

  const shouldShowBulkUploadTutorial = useMemo(() => {
    const hasCompletedBulkTutorial = getTutorialCompletedFromLocalStorage();

    return (
      !tutorial.isTutorialActive &&
      !isVideo &&
      !hasCompletedBulkTutorial &&
      mediaList.length > 1
    );
  }, [
    getTutorialCompletedFromLocalStorage,
    isVideo,
    mediaList.length,
    tutorial.isTutorialActive,
  ]);

  const shouldShowSaveSettingsTutorial = useMemo(() => {
    const hasCompletedSaveSettingsTutorial =
      getTutorialCompletedFromLocalStorage(saveSettingsTutorialId);

    return (
      !tutorial.isTutorialActive &&
      !hasCompletedSaveSettingsTutorial &&
      isLoggedIn &&
      settings.values.shouldEnableSavingAiModelDefault &&
      !isVideo
    );
  }, [
    getTutorialCompletedFromLocalStorage,
    isLoggedIn,
    isVideo,
    saveSettingsTutorialId,
    settings.values.shouldEnableSavingAiModelDefault,
    tutorial.isTutorialActive,
  ]);

  const shouldShowNewModelsTutorial = useMemo(() => {
    const hasCompletedNewModelsTutorial =
      getTutorialCompletedFromLocalStorage(newModelsTutorialId);

    return (
      !tutorial.isTutorialActive &&
      !hasCompletedNewModelsTutorial &&
      settings.values.shouldShowNewModelsTutorial &&
      !isVideo
    );
  }, [
    getTutorialCompletedFromLocalStorage,
    isVideo,
    newModelsTutorialId,
    settings.values.shouldShowNewModelsTutorial,
    tutorial.isTutorialActive,
  ]);

  useEffect(() => {
    if (shouldShowBulkUploadTutorial) {
      setTutorial((prev) => ({
        ...prev,
        steps: bulkUploadTutorialSteps,
        id: bulkUploadTutorialId,
        isTutorialActive: true,
      }));
      setTutorialCompletedInLocalStorage();

      return;
    }

    if (shouldShowNewModelsTutorial) {
      setTutorial((prev) => ({
        ...prev,
        steps: newModelsTutorialSteps,
        id: newModelsTutorialId,
        isTutorialActive: true,
      }));
      setTutorialCompletedInLocalStorage(newModelsTutorialId);

      return;
    }

    if (shouldShowSaveSettingsTutorial) {
      setTutorial((prev) => ({
        ...prev,
        steps: saveSettingsTutorialSteps,
        id: saveSettingsTutorialId,
        isTutorialActive: true,
      }));
      setTutorialCompletedInLocalStorage(saveSettingsTutorialId);
    }
  }, [
    bulkUploadTutorialId,
    bulkUploadTutorialSteps,
    newModelsTutorialId,
    newModelsTutorialSteps,
    saveSettingsTutorialId,
    saveSettingsTutorialSteps,
    setTutorial,
    setTutorialCompletedInLocalStorage,
    shouldShowBulkUploadTutorial,
    shouldShowNewModelsTutorial,
    shouldShowSaveSettingsTutorial,
  ]);

  return {
    isAiSettingsDropdownOpen,
    newModelsTutorialId,
    saveSettingsTutorialId,
    setIsAiSettingsDropdownOpen,
  };
};
